import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContacts } from '../../hooks/useContacts';
import { X, Search } from 'lucide-react';
import { useAnonymization } from '../../utils/anonymize';
import { useUserSettings } from '../../hooks/useUserSettings';

interface ContactSearchPopoverProps {
  onClose: () => void;
}

export default function ContactSearchPopover({ onClose }: ContactSearchPopoverProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { contacts } = useContacts();
  const navigate = useNavigate();
  const { settings } = useUserSettings();
  const shouldAnonymize = settings?.anonymizing_mode || false;

  // Filter contacts based on search term
  const filteredContacts = contacts.filter(contact => {
    const searchLower = searchTerm.toLowerCase();
    return (
      contact.first_name.toLowerCase().includes(searchLower) ||
      contact.last_name.toLowerCase().includes(searchLower) ||
      contact.company?.name.toLowerCase().includes(searchLower)
    );
  }).slice(0, 10); // Limit to first 10 results

  // Reset selected index when filtered results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm]);

  // Handle keyboard navigation
  useEffect(() => {
    const input = document.getElementById('contact-search-input');
    if (input) {
      input.focus();
    }
  }, []);

  const handleSelect = (contactId: string) => {
    navigate(`/contacts/${contactId}`);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredContacts.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredContacts[selectedIndex]) {
          handleSelect(filteredContacts[selectedIndex].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-[20vh]">
      <div className="bg-dark-800 rounded-lg w-full max-w-lg shadow-lg">
        <div className="p-4 border-b border-dark-700 flex items-center gap-2">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            id="contact-search-input"
            type="text"
            className="flex-1 bg-transparent border-none focus:outline-none text-white placeholder-gray-500"
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            onClick={onClose}
            className="p-1 hover:bg-dark-700 rounded-full"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <div className="max-h-[60vh] overflow-y-auto">
          {filteredContacts.map((contact, index) => (
            <button
              key={contact.id}
              className={`w-full px-4 py-3 text-left flex items-center justify-between ${
                index === selectedIndex ? 'bg-dark-700' : 'hover:bg-dark-700'
              }`}
              onClick={() => handleSelect(contact.id)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div>
                <div className="text-white">
                  {useAnonymization(contact.first_name, 'name', shouldAnonymize)}{' '}
                  {useAnonymization(contact.last_name, 'name', shouldAnonymize)}
                </div>
                {contact.company && (
                  <div className="text-sm text-gray-400">
                    {useAnonymization(contact.company.name, 'company', shouldAnonymize)}
                  </div>
                )}
              </div>
            </button>
          ))}
          {filteredContacts.length === 0 && searchTerm && (
            <div className="px-4 py-3 text-gray-400">
              No contacts found
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 